import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Map, CalendarCheck, CheckCircle2 } from 'lucide-react';
import KineticText from '../KineticText';

const HERO_IMG =
  'https://images.unsplash.com/photo-1771054244002-4445dc1da2eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMHByb2Zlc3Npb25hbCUyMHdvcmtpbmclMjBsYXB0b3AlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzg4MTA2OTk3fDA&ixlib=rb-4.1.0&q=85';

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-mist pt-32 md:pt-40" data-testid="hero-section">
      <div className="bg-grid absolute inset-0" />
      <div className="absolute -left-40 top-16 h-[28rem] w-[28rem] rounded-full bg-coral/10 blur-3xl" />
      <div className="absolute -right-40 bottom-0 h-[24rem] w-[24rem] rounded-full bg-navy/5 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-32">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2.5 rounded-full border border-navy/10 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70 shadow-card"
              data-testid="hero-trust-badge"
            >
              <span className="h-2 w-2 rounded-full bg-coral" />
              Career Guidance • Skill Development • Career Planning
            </motion.div>

            <h1 className="mt-8 font-display text-5xl font-extrabold tracking-tighter text-navy md:text-6xl lg:text-7xl" data-testid="hero-headline">
              <KineticText lines={['Build Your', 'Career With', { text: 'Clarity.', accent: true }]} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg"
              data-testid="hero-subheadline"
            >
              From choosing the right stream and college to developing the skills employers need, CareerWithRaaj helps
              you make smarter career decisions at every stage.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Link
                to="/book-consultation"
                data-testid="hero-book-consultation-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-coral px-8 py-4 text-base font-semibold text-white shadow-card-hover transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep"
              >
                Book a Career Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/career-paths"
                data-testid="hero-explore-paths-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy/15 bg-white/60 px-8 py-4 text-base font-semibold text-navy backdrop-blur transition-[border-color,color,transform] duration-200 hover:-translate-y-0.5 hover:border-navy hover:text-navy"
              >
                Explore Career Paths
              </Link>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-navy/70"
            >
              {['Stream to Career', 'Personalised Roadmaps', 'Skill-First Approach'].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-coral" /> {item}
                </li>
              ))}
            </motion.ul>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: yImage }} className="relative">
              <div className="overflow-hidden rounded-3xl shadow-card-hover">
                <motion.img
                  initial={{ scale: 1.18 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
                  src={HERO_IMG}
                  alt="Young professional planning their career journey"
                  className="h-[400px] w-full object-cover md:h-[520px]"
                  data-testid="hero-image"
                />
              </div>

              <motion.div
                style={{ y: yBadge }}
                className="absolute -left-4 top-8 flex items-center gap-3 rounded-2xl border border-white/60 bg-white/85 px-5 py-4 shadow-card-hover backdrop-blur-xl md:-left-10"
                data-testid="hero-badge-roadmap"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy">
                  <Map className="h-5 w-5 text-coral" />
                </span>
                <div>
                  <p className="text-sm font-bold text-navy">Personalised Roadmap</p>
                  <p className="text-xs text-slate-500">School → Career Growth</p>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
                className="absolute -bottom-6 right-2 flex items-center gap-3 rounded-2xl border border-white/60 bg-white/85 px-5 py-4 shadow-card-hover backdrop-blur-xl md:-right-6"
                data-testid="hero-badge-consultation"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-coral">
                  <CalendarCheck className="h-5 w-5 text-white" />
                </span>
                <div>
                  <p className="text-sm font-bold text-navy">1-to-1 Expert Guidance</p>
                  <p className="text-xs text-slate-500">At every career stage</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
